/**
 * Select-only combobox behaviour (WAI-ARIA APG). DOM focus stays on the
 * combobox; options are listbox children referenced via aria-activedescendant.
 */
import {
	type Accessor,
	createEffect,
	createSignal,
	type JSX,
	onCleanup,
} from "solid-js";
import {
	type AnchoredPopupPlacement,
	useAnchoredPopupPosition,
} from "./useAnchoredPopupPosition";

export type UseSelectOnlyComboboxParams = {
	id?: string;
	disabled?: Accessor<boolean | undefined>;
	optionCount: Accessor<number>;
	selectedIndex: Accessor<number>;
	getOptionLabel: (index: number) => string;
	isOptionDisabled?: (index: number) => boolean;
	onSelect: (index: number) => void;
	placement?: Accessor<AnchoredPopupPlacement | undefined>;
	/**
	 * Keeps the listbox open this long after a choice, so the new selection
	 * can be seen before it closes. Tab still closes at once, as focus moves on.
	 */
	closeDelayMs?: number;
	/**
	 * `"options"` (default): APG select-only listbox; Tab closes the popup.
	 * `"panel"`: focusable custom content in the listbox; Tab cycles within it.
	 */
	mode?: "options" | "panel";
};

const PANEL_FOCUSABLE_SELECTOR =
	'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type SelectOnlyComboboxOptionProps = {
	id: string;
	role: "option";
	"aria-selected": boolean;
	"aria-disabled"?: boolean;
	onMouseEnter: () => void;
	onClick: (event: MouseEvent) => void;
};

/** ARIA for the combobox trigger; spread onto a focusable `div` or `button`. */
export type SelectOnlyComboboxTriggerProps = {
	role: "combobox";
	"aria-autocomplete": "none";
	"aria-expanded": boolean;
	"aria-controls": string;
	"aria-activedescendant"?: string;
};

export type SelectOnlyComboboxListboxProps = {
	id: string;
	role: "listbox";
};

export const useSelectOnlyCombobox = (params: UseSelectOnlyComboboxParams) => {
	const comboboxId =
		params.id ??
		`select-only-combobox-${Math.random().toString(36).slice(2, 11)}`;
	const listboxId = `${comboboxId}-listbox`;

	const [isOpen, setIsOpen] = createSignal(false);
	const [isClosingAfterSelect, setIsClosingAfterSelect] = createSignal(false);
	const [focusedIndex, setFocusedIndex] = createSignal(-1);

	let triggerEl: HTMLElement | undefined;
	let listboxEl: HTMLDivElement | undefined;
	let rootEl: HTMLDivElement | undefined;
	let typeaheadBuffer = "";
	let typeaheadLastKeyAt = 0;
	let pendingClose: ReturnType<typeof setTimeout> | undefined;

	const disabled = () => Boolean(params.disabled?.());
	const isPanelMode = () => params.mode === "panel";

	const getPanelFocusables = (): HTMLElement[] => {
		if (!listboxEl) {
			return [];
		}
		return Array.from(
			listboxEl.querySelectorAll<HTMLElement>(PANEL_FOCUSABLE_SELECTOR),
		).filter(
			(el) =>
				!el.closest("[hidden]") && !el.closest("[aria-hidden='true']"),
		);
	};

	const optionId = (index: number) => `${listboxId}-option-${index}`;

	const isIndexDisabled = (index: number) =>
		params.isOptionDisabled?.(index) ?? false;

	const getNextEnabledIndex = (
		startIndex: number,
		direction: number,
	): number => {
		const count = params.optionCount();
		if (count === 0) {
			return -1;
		}
		let index = startIndex;
		for (let iterations = 0; iterations < count; iterations++) {
			index += direction;
			if (index < 0) {
				index = count - 1;
			}
			if (index >= count) {
				index = 0;
			}
			if (!isIndexDisabled(index)) {
				return index;
			}
		}
		return startIndex >= 0 && !isIndexDisabled(startIndex) ? startIndex : -1;
	};

	const initialFocusedIndex = () => {
		const selected = params.selectedIndex();
		if (selected >= 0 && !isIndexDisabled(selected)) {
			return selected;
		}
		return getNextEnabledIndex(-1, 1);
	};

	const cancelPendingClose = () => {
		clearTimeout(pendingClose);
		pendingClose = undefined;
		setIsClosingAfterSelect(false);
	};

	const close = () => {
		cancelPendingClose();
		setIsOpen(false);
		setFocusedIndex(-1);
	};

	const open = (focusIndex?: number) => {
		cancelPendingClose();
		const index =
			focusIndex ?? initialFocusedIndex() ?? getNextEnabledIndex(-1, 1);
		setIsOpen(true);
		setFocusedIndex(index);
		setTimeout(() => {
			updatePosition();
			if (isPanelMode()) {
				getPanelFocusables()[0]?.focus({ preventScroll: true });
			} else {
				triggerEl?.focus();
			}
		}, 0);
	};

	const toggle = () => {
		if (isOpen()) {
			close();
		} else {
			open();
		}
	};

	const commitSelection = (
		index: number,
		options?: { refocusTrigger?: boolean },
	) => {
		if (index < 0 || index >= params.optionCount() || isIndexDisabled(index)) {
			return;
		}
		setFocusedIndex(index);
		params.onSelect(index);
		const refocusTrigger = options?.refocusTrigger !== false;
		if (refocusTrigger && params.closeDelayMs) {
			cancelPendingClose();
			pendingClose = setTimeout(close, params.closeDelayMs);
			setIsClosingAfterSelect(true);
		} else {
			close();
		}
		if (refocusTrigger) {
			triggerEl?.focus();
		}
	};

	const widgetOwnsFocus = () => {
		const active = document.activeElement;
		if (active == null) {
			return false;
		}
		if (active === triggerEl) {
			return true;
		}
		return (
			(rootEl != null && rootEl.contains(active)) ||
			(listboxEl != null && listboxEl.contains(active))
		);
	};

	const handlePanelTab = (event: KeyboardEvent) => {
		const focusables = getPanelFocusables();
		if (focusables.length === 0) {
			return;
		}
		const active = document.activeElement;
		if (!(active instanceof HTMLElement) || !listboxEl?.contains(active)) {
			return;
		}
		event.preventDefault();
		const index = focusables.indexOf(active);
		if (event.shiftKey) {
			if (index <= 0) {
				focusables[focusables.length - 1]?.focus();
			} else {
				focusables[index - 1]?.focus();
			}
			return;
		}
		if (index < 0 || index >= focusables.length - 1) {
			focusables[0]?.focus();
		} else {
			focusables[index + 1]?.focus();
		}
	};

	const handleTabWhileOpen = (event: KeyboardEvent) => {
		if (event.defaultPrevented) {
			return;
		}
		if (isPanelMode()) {
			const active = document.activeElement;
			if (
				listboxEl &&
				active instanceof HTMLElement &&
				listboxEl.contains(active)
			) {
				handlePanelTab(event);
				return;
			}
			if (active === triggerEl) {
				close();
			}
			event.stopPropagation();
			return;
		}
		const index = focusedIndex();
		if (index >= 0) {
			commitSelection(index, { refocusTrigger: false });
		} else {
			close();
		}
		// APG: Tab closes the listbox and moves to the next tab stop; do not
		// preventDefault or refocus the combobox.
		event.stopPropagation();
	};

	const findTypeaheadIndex = (query: string, startAfter: number): number => {
		const normalized = query.toLowerCase();
		if (normalized.length === 0) {
			return -1;
		}
		const count = params.optionCount();
		const start = startAfter + 1;
		for (let offset = 0; offset < count; offset++) {
			const index = (start + offset) % count;
			if (isIndexDisabled(index)) {
				continue;
			}
			if (params.getOptionLabel(index).toLowerCase().startsWith(normalized)) {
				return index;
			}
		}
		return -1;
	};

	const handleTypeahead = (char: string) => {
		const now = Date.now();
		if (now - typeaheadLastKeyAt > 500) {
			typeaheadBuffer = "";
		}
		typeaheadLastKeyAt = now;
		if (
			typeaheadBuffer.length === 1 &&
			typeaheadBuffer.toLowerCase() === char.toLowerCase()
		) {
			const from = isOpen() ? focusedIndex() : params.selectedIndex();
			const next = findTypeaheadIndex(char, from);
			if (next >= 0) {
				if (!isOpen()) {
					open(next);
				} else {
					setFocusedIndex(next);
				}
			}
			return;
		}
		typeaheadBuffer += char;
		const match = findTypeaheadIndex(typeaheadBuffer, -1);
		if (match >= 0) {
			if (!isOpen()) {
				open(match);
			} else {
				setFocusedIndex(match);
			}
		}
	};

	const jumpFocusedIndex = (delta: number) => {
		let index = focusedIndex();
		if (index < 0) {
			index = initialFocusedIndex();
		}
		let remaining = Math.abs(delta);
		const direction = delta > 0 ? 1 : -1;
		while (remaining > 0 && index >= 0) {
			const next = getNextEnabledIndex(index, direction);
			if (next === index) {
				break;
			}
			index = next;
			remaining--;
		}
		if (index >= 0) {
			setFocusedIndex(index);
		}
	};

	const onTriggerKeyDown: JSX.EventHandlerUnion<
		HTMLElement,
		KeyboardEvent
	> = (event) => {
		if (disabled()) {
			return;
		}
		const e = event;

		if (e.altKey && e.key === "ArrowDown") {
			e.preventDefault();
			if (!isOpen()) {
				open();
			}
			return;
		}

		if (e.altKey && e.key === "ArrowUp" && isOpen()) {
			e.preventDefault();
			const index = focusedIndex();
			if (index >= 0) {
				commitSelection(index);
			}
			return;
		}

		switch (e.key) {
			case "Enter":
			case " ":
				e.preventDefault();
				if (isOpen()) {
					const index = focusedIndex();
					if (index >= 0) {
						commitSelection(index);
					} else {
						const first = getNextEnabledIndex(-1, 1);
						if (first >= 0) {
							commitSelection(first);
						}
					}
				} else {
					open();
				}
				break;

			case "Escape":
				e.preventDefault();
				close();
				triggerEl?.focus();
				break;

			case "ArrowDown":
				e.preventDefault();
				if (!isOpen()) {
					open(getNextEnabledIndex(-1, 1));
				} else {
					setFocusedIndex(getNextEnabledIndex(focusedIndex(), 1));
				}
				break;

			case "ArrowUp":
				e.preventDefault();
				if (!isOpen()) {
					open(getNextEnabledIndex(params.optionCount(), -1));
				} else {
					setFocusedIndex(getNextEnabledIndex(focusedIndex(), -1));
				}
				break;

			case "Home":
				e.preventDefault();
				if (!isOpen()) {
					open(getNextEnabledIndex(-1, 1));
				} else {
					setFocusedIndex(getNextEnabledIndex(-1, 1));
				}
				break;

			case "End":
				e.preventDefault();
				if (!isOpen()) {
					open(getNextEnabledIndex(params.optionCount(), -1));
				} else {
					setFocusedIndex(getNextEnabledIndex(params.optionCount(), -1));
				}
				break;

			case "PageDown":
				if (!isOpen()) {
					return;
				}
				e.preventDefault();
				jumpFocusedIndex(10);
				break;

			case "PageUp":
				if (!isOpen()) {
					return;
				}
				e.preventDefault();
				jumpFocusedIndex(-10);
				break;

				case "Tab":
					if (isOpen()) {
						handleTabWhileOpen(e);
					}
					break;

			default:
				if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
					e.preventDefault();
					handleTypeahead(e.key);
				}
				break;
		}
	};

	const onTriggerClick: JSX.EventHandlerUnion<HTMLElement, MouseEvent> = (
		event,
	) => {
		event.preventDefault();
		event.stopPropagation();
		if (disabled()) {
			return;
		}
		toggle();
	};

	const triggerProps = (): SelectOnlyComboboxTriggerProps => ({
		role: "combobox",
		"aria-autocomplete": "none",
		"aria-expanded": isOpen(),
		"aria-controls": listboxId,
		"aria-activedescendant":
			isOpen() && focusedIndex() >= 0 ? optionId(focusedIndex()) : undefined,
	});

	const listboxProps = (): SelectOnlyComboboxListboxProps => ({
		id: listboxId,
		role: "listbox",
	});

	const getOptionProps = (index: number): SelectOnlyComboboxOptionProps => ({
		id: optionId(index),
		role: "option",
		"aria-selected": isOpen() && index === focusedIndex(),
		"aria-disabled": isIndexDisabled(index) ? true : undefined,
		onMouseEnter: () => {
			if (!isIndexDisabled(index)) {
				setFocusedIndex(index);
			}
		},
		onClick: (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (!isIndexDisabled(index)) {
				commitSelection(index);
			}
		},
	});

	const { position, updatePosition } = useAnchoredPopupPosition({
		isOpen,
		placement: () => params.placement?.(),
		getTriggerEl: () => triggerEl,
		getListboxEl: () => listboxEl,
	});

	createEffect(() => {
		if (disabled()) {
			close();
		}
	});

	onCleanup(cancelPendingClose);

	createEffect(() => {
		if (isOpen() && focusedIndex() >= 0) {
			const focusedElement = document.getElementById(optionId(focusedIndex()));
			if (typeof focusedElement?.scrollIntoView === "function") {
				focusedElement.scrollIntoView({ block: "nearest" });
			}
		}
	});

	createEffect(() => {
		if (!isOpen()) {
			return;
		}

		const handleClickOutside = (event: MouseEvent) => {
			const path = event.composedPath();
			if (rootEl && path.includes(rootEl)) {
				return;
			}
			if (listboxEl && path.includes(listboxEl)) {
				return;
			}
			close();
		};

		const handleDocumentKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				close();
				triggerEl?.focus();
			}
		};

		const handleCaptureKeyDown = (event: KeyboardEvent) => {
			if (!isOpen() || event.key !== "Tab") {
				return;
			}
			if (!widgetOwnsFocus()) {
				return;
			}
			handleTabWhileOpen(event);
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleDocumentKeyDown);
		document.addEventListener("keydown", handleCaptureKeyDown, true);
		onCleanup(() => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleDocumentKeyDown);
			document.removeEventListener("keydown", handleCaptureKeyDown, true);
		});
	});

	return {
		comboboxId,
		listboxId,
		isOpen,
		/** True while the listbox is held open by `closeDelayMs`. */
		isClosingAfterSelect,
		focusedIndex,
		open,
		close,
		toggle,
		triggerProps,
		listboxProps,
		getOptionProps,
		onTriggerKeyDown,
		onTriggerClick,
		popupPosition: position,
		setTriggerEl: (el: HTMLElement | undefined) => {
			triggerEl = el;
		},
		setListboxEl: (el: HTMLDivElement | undefined) => {
			listboxEl = el;
		},
		setRootEl: (el: HTMLDivElement | undefined) => {
			rootEl = el;
		},
		updatePosition,
	};
};
